import type { EngineRequest } from './validate-api';
import type { ValidateTurn } from './validate-runs';

/**
 * Which bot reply each engine request belongs to.
 *
 * A request answers the last customer message sent at or before it — with
 * `skewMs` of slack, because the browser's clock and the log server's don't
 * agree exactly — and anything before the first message is the greeting.
 * One message can set off several requests (the engine retries a failed
 * action node, then looks for an error handler), so a reply can have more
 * than one.
 *
 * Returns request lists keyed by the transcript index of the bot reply they
 * belong to. A message with no reply yet has nowhere to show, so its
 * requests wait until the reply lands.
 */
export const matchTraceToReplies = (
  requests: EngineRequest[],
  transcript: ValidateTurn[],
  skewMs = 1500
): Record<string, EngineRequest[]> => {
  const userIdx = transcript
    .map((t, i) => (t.role === 'user' ? i : -1))
    .filter((i) => i >= 0);
  const sentAt = userIdx.map((i) => transcript[i].at);
  // A transcript saved before messages were stamped can't be matched.
  if (sentAt.some((t) => typeof t !== 'number')) return {};

  // The bot reply after position `from` (a user message, or -1 for the start).
  const replyAfter = (from: number): number | undefined => {
    for (let i = from + 1; i < transcript.length; i += 1) {
      if (transcript[i].role === 'user') return undefined;
      if (transcript[i].role === 'bot') return i;
    }
    return undefined;
  };

  const out: Record<string, EngineRequest[]> = {};
  requests.forEach((req) => {
    let k = -1;
    sentAt.forEach((t, j) => {
      if (req.at >= (t as number) - skewMs) k = j;
    });
    const reply = replyAfter(k === -1 ? -1 : userIdx[k]);
    if (reply === undefined) return;
    (out[String(reply)] ??= []).push(req);
  });
  return out;
};

/**
 * A reply's requests without the setup walks before the one that answered.
 * Within one reply, a walk that stopped to wait and was followed by another
 * was resumed by the platform, not the customer — the Supervisor asking for
 * the conversation so far (`BotToPlatform` from node 1) — so it says nothing
 * about this reply. Walks that failed stay: they explain what came next.
 */
export const relevantRequests = (requests: EngineRequest[]): EngineRequest[] =>
  requests.filter(
    (req, i) =>
      i === requests.length - 1 ||
      req.outcome !== 'waiting' ||
      req.stoppedAt === undefined
  );

/** Where to start looking in the logs: a little before the chat's first message. */
export const traceSince = (transcript: ValidateTurn[]): number => {
  const first = transcript.find((t) => typeof t.at === 'number')?.at;
  return (first ?? Date.now()) - 2 * 60_000;
};

/**
 * The agents a chat's engine requests walked through, in the order it first
 * reached each, from the nodes' group membership (`nodeAgent`: node number →
 * agent id). Nodes in no agent are skipped.
 */
export const agentsReached = (
  requests: EngineRequest[],
  nodeAgent: Map<string, string>
): string[] => {
  const seen: string[] = [];
  requests.forEach((req) =>
    req.steps.forEach((step) => {
      const agent = nodeAgent.get(String(step.node));
      if (agent && !seen.includes(agent)) seen.push(agent);
    })
  );
  return seen;
};

export type RoutingVerdict =
  | { status: 'unknown' }
  | { status: 'pass' }
  | { status: 'fail'; reachedId: string | null };

/**
 * Did the chat reach the agent it was expected to? A fact read off the
 * trace, not a judgment: `pass` if the expected agent's nodes ran; `fail`
 * with the use-case agent it went to instead (the first reached that isn't
 * one of `coreIds`, the Supervisor and Knowledge Agent every chat passes
 * through), or null when it reached none.
 */
export const routingVerdict = ({
  expectedId,
  reached,
  coreIds,
  hasTrace,
}: {
  expectedId: string;
  reached: string[];
  coreIds: Set<string>;
  hasTrace: boolean;
}): RoutingVerdict => {
  if (!hasTrace) return { status: 'unknown' };
  if (reached.includes(expectedId)) return { status: 'pass' };
  const instead =
    reached.find((id) => !coreIds.has(id)) ??
    reached.find((id) => id !== expectedId) ??
    null;
  return { status: 'fail', reachedId: instead };
};
